import type {
  DownloadsConfig,
  ReportSectionId,
  VisibleReportSection,
  WizardAnswers,
} from "@/types";
import { asList, hasProduct } from "@/engine/answers";
import { isNordicLocale } from "@/config/loadConfig";
import { localizedKnowledgeBaseUrl } from "@/config/localizedUrls";

function section(
  id: ReportSectionId,
  steps: VisibleReportSection["steps"],
): VisibleReportSection {
  return {
    id,
    titleKey: `report.${id}.title`,
    subtitleKey: id === "licensing" || id === "support" ? undefined : `report.${id}.subtitle`,
    steps,
  };
}

export function buildReportSections(
  answers: WizardAnswers,
  downloads: DownloadsConfig,
  locale: string,
): VisibleReportSection[] {
  const sections: VisibleReportSection[] = [];
  const rwVersions = asList(answers.rw_version);
  const mvVersions = asList(answers.mv_version);
  const mvAssistPlatforms = asList(answers.mv_assist_platforms);
  const tasksOs = asList(answers.tasks_os);
  const isSingle = answers.rw_license === "single";
  const isGroup = answers.rw_license === "group";
  const nordic = isNordicLocale(locale);
  const rwWindowsDownloadHref = localizedKnowledgeBaseUrl(downloads.rwWindowsKnowledgeBase, locale);
  const rwMacDownloadHref = localizedKnowledgeBaseUrl(downloads.rwMacKnowledgeBase, locale);
  const rwIosSystemRequirementsHref = localizedKnowledgeBaseUrl(downloads.rwIosSystemRequirements, locale);
  const rwIosDownloadHref = localizedKnowledgeBaseUrl(downloads.rwIosKnowledgeBase, locale);
  const rwIosMdmGuideHref = localizedKnowledgeBaseUrl(downloads.rwIosMdmGuide, locale);
  const rwIosAllowlistHref = localizedKnowledgeBaseUrl(downloads.rwIosAllowlist, locale);
  const rwAndroidSystemRequirementsHref = localizedKnowledgeBaseUrl(downloads.rwAndroidSystemRequirements, locale);
  const rwAndroidDownloadHref = localizedKnowledgeBaseUrl(downloads.rwAndroidKnowledgeBase, locale);
  const rwAndroidDeploymentGuideHref = localizedKnowledgeBaseUrl(downloads.rwAndroidDeploymentGuide, locale);
  const mindviewWindowsDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewWindowsKnowledgeBase,
    locale,
  );
  const mindviewMacDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewMacKnowledgeBase,
    locale,
  );
  const mindviewAssistAndroidSystemRequirementsHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistAndroidSystemRequirements,
    locale,
  );
  const mindviewAssistAndroidDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistAndroidKnowledgeBase,
    locale,
  );
  const mindviewAssistIosSystemRequirementsHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistIosSystemRequirements,
    locale,
  );
  const mindviewAssistIosDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistIosKnowledgeBase,
    locale,
  );
  const tasksWindowsDownloadHref = localizedKnowledgeBaseUrl(
    downloads.tasksWindowsKnowledgeBase,
    locale,
  );
  const tasksMacDownloadHref = localizedKnowledgeBaseUrl(
    downloads.tasksMacKnowledgeBase,
    locale,
  );

  const isEnterprise = answers.rw_license === "enterprise";
  if (
    (isGroup && (hasProduct(answers, "rw") || hasProduct(answers, "mindview") || hasProduct(answers, "tasks"))) ||
    (isEnterprise && hasProduct(answers, "mindview"))
  ) {
    const steps: VisibleReportSection["steps"] = [];
    if (hasProduct(answers, "rw")) {
      steps.push({
        id: "admin-tool",
        titleKey: "report.licensing.adminTool.title",
        descriptionKey: "report.licensing.adminTool.description",
        linkLabelKey: "report.licensing.adminTool.link",
        docRef: 1,
      });
    }
    if (hasProduct(answers, "mindview")) {
      steps.push({
        id: "mv-user-mgmt",
        titleKey: "report.licensing.mvUserMgmt.title",
        descriptionKey: "report.licensing.mvUserMgmt.description",
        linkLabelKey: "report.licensing.mvUserMgmt.guideLink",
        docRef: 2,
        href: downloads.mindviewUserManagement,
      });
    }
    if (hasProduct(answers, "tasks")) {
      steps.push({
        id: "tasks-licensing",
        titleKey: "report.licensing.tasks.title",
        descriptionKey: "report.licensing.tasks.description",
        href: downloads.tasks,
      });
    }
    if (steps.length > 0) sections.push(section("licensing", steps));
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("win") && isSingle) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "rw-single-sys",
        titleKey: "report.rwWindows.sysReq.title",
        descriptionKey: "report.rwWindows.sysReq.description",
        linkLabelKey: "report.rwWindows.sysReq.link",
        docRef: 3,
      },
      {
        id: "rw-single-download",
        titleKey: "report.rwWindows.download.knowledgeBase.title",
        descriptionKey: "report.rwWindows.download.knowledgeBase.description",
        linkLabelKey: "report.rwWindows.download.knowledgeBase.link",
        href: rwWindowsDownloadHref,
        noteKey: "report.rwWindows.download.singleBrowserExtNote",
        noteLinkLabelKey: "report.rwWindows.download.singleBrowserExtLink",
        noteDocRef: 37,
      },
    ];
    if (nordic) {
      steps.push({
        id: "rw-single-nordic",
        titleKey: "report.rwWindows.nordicPack.title",
        descriptionKey: "report.rwWindows.nordicPack.body",
        linkLabelKey: "report.rwWindows.nordicPack.link",
        href: downloads.rwNordicLanguagePack,
      });
    }
    steps.push({
      id: "rw-single-activate",
      titleKey: "report.rwSingle.activate.title",
      descriptionKey: "report.rwSingle.activate.description",
      linkLabelKey: "report.rwSingle.activate.articleLink",
      docRef: 4,
    });
    sections.push(section("rwSingle", steps));
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("win") && !isSingle) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "rw-win-sys",
        titleKey: "report.rwWindows.sysReq.title",
        descriptionKey: "report.rwWindows.sysReq.description",
        linkLabelKey: "report.rwWindows.sysReq.link",
        docRef: 3,
      },
      {
        id: "rw-win-download",
        titleKey: "report.rwWindows.download.knowledgeBase.title",
        descriptionKey: "report.rwWindows.download.knowledgeBase.description",
        linkLabelKey: "report.rwWindows.download.knowledgeBase.link",
        href: rwWindowsDownloadHref,
        docRef: 5,
      },
    ];
    steps.push({
      id: "rw-win-browser-extension",
      titleKey: "report.rwWindows.browserExt.title",
      descriptionKey: "report.rwWindows.browserExt.description",
      linkLabelKey: "report.rwWindows.browserExt.link",
      docRef: 36,
    });
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "rw-win-allowlist",
        titleKey: "report.rwWindows.allowlist.title",
        descriptionKey: "report.rwWindows.allowlist.description",
        linkLabelKey: "report.rwWindows.allowlist.link",
        docRef: 6,
      });
    }
    if (answers.login_method === "auto") {
      steps.push({
        id: "rw-win-sso",
        titleKey: "report.rwWindows.autoSignIn.title",
        descriptionKey: "report.rwWindows.autoSignIn.description",
        linkLabelKey: "report.rwWindows.autoSignIn.link",
        docRef: 33,
      });
    }
    if (answers.offline === "yes") {
      steps.push({
        id: "rw-win-offline",
        titleKey: "report.rwWindows.offline.title",
        descriptionKey: "report.rwWindows.offline.description",
        linkLabelKey: "report.rwWindows.offline.link",
        docRef: 9,
      });
    }
    if (nordic) {
      steps.push({
        id: "rw-win-nordic",
        titleKey: "report.rwWindows.nordicPack.title",
        descriptionKey: "report.rwWindows.nordicPack.body",
        linkLabelKey: "report.rwWindows.nordicPack.link",
        href: downloads.rwNordicLanguagePack,
      });
    }
    sections.push(section("rwWindows", steps));
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("mac") && isSingle) {
    sections.push(
      section("rwSingleMac", [
        {
          id: "rw-mac-single-sys",
          titleKey: "report.rwSingleMac.sysReq.title",
          descriptionKey: "report.rwSingleMac.sysReq.description",
          linkLabelKey: "report.rwSingleMac.sysReq.link",
          docRef: 10,
        },
        {
          id: "rw-mac-single-download",
          titleKey: "report.rwSingleMac.download.title",
          descriptionKey: "report.rwSingleMac.download.description",
          linkLabelKey: "report.rwSingleMac.download.link",
          href: rwMacDownloadHref,
        },
        {
          id: "rw-mac-single-activate",
          titleKey: "report.rwSingleMac.activate.title",
          descriptionKey: "report.rwSingleMac.activate.description",
          linkLabelKey: "report.rwSingleMac.activate.articleLink",
          docRef: 4,
        },
      ]),
    );
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("mac") && !isSingle) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "rw-mac-sys",
        titleKey: "report.rwMac.sysReq.title",
        descriptionKey: "report.rwMac.sysReq.description",
        linkLabelKey: "report.rwMac.sysReq.link",
        docRef: 10,
      },
      {
        id: "rw-mac-download",
        titleKey: "report.rwMac.download.title",
        descriptionKey: "report.rwMac.download.description",
        linkLabelKey: "report.rwMac.download.link",
        href: rwMacDownloadHref,
      },
      {
        id: "rw-mac-deploy",
        titleKey: "report.rwMac.deploy.title",
        descriptionKey: "report.rwMac.deploy.description",
        linkLabelKey: "report.rwMac.deploy.link",
        docRef: 11,
      },
    ];
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "rw-mac-allowlist",
        titleKey: "report.rwMac.allowlist.title",
        descriptionKey: "report.rwMac.allowlist.description",
        linkLabelKey: "report.rwMac.allowlist.link",
        docRef: 12,
      });
    }
    if (answers.login_method === "auto") {
      steps.push({
        id: "rw-mac-sso",
        titleKey: "report.rwMac.autoSignIn.title",
        descriptionKey: "report.rwMac.autoSignIn.description",
        linkLabelKey: "report.rwMac.autoSignIn.link",
        docRef: 7,
      });
    }
    if (answers.use_on_webpages === "yes") {
      steps.push({
        id: "rw-mac-ext",
        titleKey: "report.rwMac.browserExt.title",
        descriptionKey: "report.rwMac.browserExt.description",
      });
    }
    if (answers.offline === "yes") {
      steps.push({
        id: "rw-mac-offline",
        titleKey: "report.rwMac.offline.title",
        descriptionKey: "report.rwMac.offline.description",
        linkLabelKey: "report.rwMac.offline.link",
        docRef: 13,
      });
    }
    if (nordic) {
      steps.push({
        id: "rw-mac-nordic",
        titleKey: "report.rwWindows.nordicPack.title",
        descriptionKey: "report.rwWindows.nordicPack.body",
        linkLabelKey: "report.rwWindows.nordicPack.link",
        href: downloads.rwNordicLanguagePack,
      });
    }
    sections.push(section("rwMac", steps));
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("chrome_edge") && !isSingle) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "rw-chrome-sys",
        titleKey: "report.rwChrome.sysReq.title",
        descriptionKey: "report.rwChrome.sysReq.description",
        linkLabelKey: "report.rwChrome.sysReq.link",
        docRef: 14,
      },
      {
        id: "rw-chrome-deploy",
        titleKey: "report.rwChrome.chrome.title",
        descriptionKey: "report.rwChrome.chrome.description",
        linkLabelKey: "report.rwChrome.chrome.link",
        docRef: 15,
      },
      {
        id: "rw-edge-deploy",
        titleKey: "report.rwChrome.edge.title",
        descriptionKey: "report.rwChrome.edge.description",
        linkLabelKey: "report.rwChrome.edge.link",
        docRef: 16,
      },
    ];
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "rw-chrome-allowlist",
        titleKey: "report.rwChrome.allowlist.title",
        descriptionKey: "report.rwChrome.allowlist.description",
        linkLabelKey: "report.rwChrome.allowlist.chromeLink",
        docRef: 17,
      });
      steps.push({
        id: "rw-edge-allowlist",
        titleKey: "report.rwChrome.allowlist.title",
        descriptionKey: "report.rwChrome.allowlist.description",
        linkLabelKey: "report.rwChrome.allowlist.edgeLink",
        docRef: 18,
      });
    }
    sections.push(section("rwChrome", steps));
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("ios") && !isSingle) {
    sections.push(
      section("rwIos", [
        {
          id: "rw-ios-sys",
          titleKey: "report.rwIos.sysReq.title",
          descriptionKey: "report.rwIos.sysReq.description",
          linkLabelKey: "report.rwIos.sysReq.link",
          href: rwIosSystemRequirementsHref,
        },
        {
          id: "rw-ios-download",
          titleKey: "report.rwIos.download.title",
          descriptionKey: "report.rwIos.download.description",
          linkLabelKey: "report.rwIos.download.link",
          href: rwIosDownloadHref,
        },
        {
          id: "rw-ios-mdm",
          titleKey: "report.rwIos.mdm.title",
          descriptionKey: "report.rwIos.mdm.description",
          linkLabelKey: "report.rwIos.mdm.link",
          href: rwIosMdmGuideHref,
        },
        {
          id: "rw-ios-user-guide",
          titleKey: "report.rwIos.userGuide.title",
          descriptionKey: "report.rwIos.userGuide.description",
          linkLabelKey: "report.rwIos.userGuide.link",
          href: downloads.rwIosUserGuide,
        },
        ...(answers.network_restricted === "yes"
          ? [
              {
                id: "rw-ios-allowlist",
                titleKey: "report.rwIos.allowlist.title",
                descriptionKey: "report.rwIos.allowlist.description",
                linkLabelKey: "report.rwIos.allowlist.link",
                href: rwIosAllowlistHref,
              },
            ]
          : []),
      ]),
    );
  }

  if (hasProduct(answers, "rw") && rwVersions.includes("android") && !isSingle) {
    sections.push(
      section("rwAndroid", [
        {
          id: "rw-android-sys",
          titleKey: "report.rwAndroid.sysReq.title",
          descriptionKey: "report.rwAndroid.sysReq.description",
          linkLabelKey: "report.rwAndroid.sysReq.link",
          href: rwAndroidSystemRequirementsHref,
        },
        {
          id: "rw-android-download",
          titleKey: "report.rwAndroid.download.title",
          descriptionKey: "report.rwAndroid.download.description",
          linkLabelKey: "report.rwAndroid.download.link",
          href: rwAndroidDownloadHref,
        },
        {
          id: "rw-android-deploy",
          titleKey: "report.rwAndroid.deploy.title",
          descriptionKey: "report.rwAndroid.deploy.description",
          linkLabelKey: "report.rwAndroid.deploy.link",
          href: rwAndroidDeploymentGuideHref,
        },
        {
          id: "rw-android-user-guide",
          titleKey: "report.rwAndroid.userGuide.title",
          descriptionKey: "report.rwAndroid.userGuide.description",
          linkLabelKey: "report.rwAndroid.userGuide.link",
          href: downloads.rwAndroidUserGuide,
        },
      ]),
    );
  }

  if (hasProduct(answers, "mindview") && mvVersions.includes("win")) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "mv-win-sys",
        titleKey: "report.mvWindows.sysReq.title",
        descriptionKey: "report.mvWindows.sysReq.description",
        linkLabelKey: "report.mvWindows.sysReq.link",
        docRef: 19,
      },
      {
        id: "mv-win-download",
        titleKey: "report.mvWindows.download.title",
        descriptionKey: "report.mvWindows.download.description",
        linkLabelKey: "report.mvWindows.download.link",
        href: mindviewWindowsDownloadHref,
        docRef: 20,
      },
    ];
    if (answers.login_method === "auto") {
      steps.push({
        id: "mv-win-sso",
        titleKey: "report.mvWindows.autoSignIn.title",
        descriptionKey: "report.mvWindows.autoSignIn.description",
        linkLabelKey: "report.mvWindows.autoSignIn.link",
        docRef: 22,
      });
    }
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "mv-win-allowlist",
        titleKey: "report.mvWindows.allowlist.title",
        descriptionKey: "report.mvWindows.allowlist.description",
        linkLabelKey: "report.mvWindows.allowlist.link",
        docRef: 23,
      });
    }
    if (answers.mv_win_restrict_online_services === "yes") {
      steps.push({
        id: "mv-win-restrict",
        titleKey: "report.mvWindows.restrict.title",
        descriptionKey: "report.mvWindows.restrict.description",
        linkLabelKey: "report.mvWindows.restrict.link",
        docRef: 24,
      });
    }
    steps.push({
      id: "mv-win-azure",
      titleKey: "report.mvWindows.sso.title",
      descriptionKey: "report.mvWindows.sso.description",
      linkLabelKey: "report.mvWindows.sso.link",
      docRef: 25,
      optional: true,
    });
    sections.push(section("mvWindows", steps));
  }

  if (hasProduct(answers, "mindview") && mvVersions.includes("mac")) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "mv-mac-sys",
        titleKey: "report.mvMac.sysReq.title",
        descriptionKey: "report.mvMac.sysReq.description",
        linkLabelKey: "report.mvMac.sysReq.link",
        docRef: 26,
      },
      {
        id: "mv-mac-download",
        titleKey: "report.mvMac.download.title",
        descriptionKey: "report.mvMac.download.description",
        linkLabelKey: "report.mvMac.download.link",
        href: mindviewMacDownloadHref,
        docRef: 27,
      },
    ];
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "mv-mac-allowlist",
        titleKey: "report.mvMac.allowlist.title",
        descriptionKey: "report.mvMac.allowlist.description",
        linkLabelKey: "report.mvMac.allowlist.link",
        docRef: 23,
      });
    }
    if (answers.login_method === "auto") {
      steps.push({
        id: "mv-mac-sso",
        titleKey: "report.mvMac.autoSignIn.title",
        descriptionKey: "report.mvMac.autoSignIn.description",
        linkLabelKey: "report.mvMac.autoSignIn.link",
        docRef: 30,
      });
    }
    steps.push({
      id: "mv-mac-azure",
      titleKey: "report.mvMac.sso.title",
      descriptionKey: "report.mvMac.sso.description",
      linkLabelKey: "report.mvMac.sso.link",
      docRef: 25,
      optional: true,
    });
    if (answers.mv_win_restrict_online_services === "yes") {
      steps.push({
        id: "mv-mac-restrict",
        titleKey: "report.mvMac.restrict.title",
        descriptionKey: "report.mvMac.restrict.description",
        linkLabelKey: "report.mvMac.restrict.link",
        docRef: 24,
      });
    }
    sections.push(section("mvMac", steps));
  }

  if (hasProduct(answers, "mindview") && mvVersions.includes("online")) {
    sections.push(
      section("mvOnline", [
        {
          id: "mv-online-sys",
          titleKey: "report.mvOnline.sysReq.title",
          descriptionKey: "report.mvOnline.sysReq.description",
        },
        {
          id: "mv-online-access",
          titleKey: "report.mvOnline.access.title",
          descriptionKey: "report.mvOnline.access.description",
          linkLabelKey: "report.mvOnline.access.link",
          href: downloads.mindviewOnline,
        },
        ...(answers.network_restricted === "yes"
          ? [
              {
                id: "mv-online-allowlist",
                titleKey: "report.mvOnline.allowlist.title",
                descriptionKey: "report.mvOnline.allowlist.description",
                linkLabelKey: "report.mvOnline.allowlist.link",
                docRef: 23,
              },
            ]
          : []),
      ]),
    );
  }

  if (
    hasProduct(answers, "mindview") &&
    mvVersions.includes("assist") &&
    mvAssistPlatforms.includes("android")
  ) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "mv-assist-android-sys",
        titleKey: "report.mvAssistAndroid.sysReq.title",
        descriptionKey: "report.mvAssistAndroid.sysReq.description",
        linkLabelKey: "report.mvAssistAndroid.sysReq.link",
        href: mindviewAssistAndroidSystemRequirementsHref,
      },
      {
        id: "mv-assist-android-download",
        titleKey: "report.mvAssistAndroid.download.title",
        descriptionKey: "report.mvAssistAndroid.download.description",
        linkLabelKey: "report.mvAssistAndroid.download.link",
        href: mindviewAssistAndroidDownloadHref,
      },
    ];
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "mv-assist-android-allowlist",
        titleKey: "report.mvWindows.allowlist.title",
        descriptionKey: "report.mvWindows.allowlist.description",
        linkLabelKey: "report.mvWindows.allowlist.link",
        docRef: 23,
      });
    }
    sections.push(section("mvAssistAndroid", steps));
  }

  if (
    hasProduct(answers, "mindview") &&
    mvVersions.includes("assist") &&
    mvAssistPlatforms.includes("ios")
  ) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "mv-assist-ios-sys",
        titleKey: "report.mvAssistIos.sysReq.title",
        descriptionKey: "report.mvAssistIos.sysReq.description",
        linkLabelKey: "report.mvAssistIos.sysReq.link",
        href: mindviewAssistIosSystemRequirementsHref,
      },
      {
        id: "mv-assist-ios-download",
        titleKey: "report.mvAssistIos.download.title",
        descriptionKey: "report.mvAssistIos.download.description",
        linkLabelKey: "report.mvAssistIos.download.link",
        href: mindviewAssistIosDownloadHref,
      },
    ];
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "mv-assist-ios-allowlist",
        titleKey: "report.mvWindows.allowlist.title",
        descriptionKey: "report.mvWindows.allowlist.description",
        linkLabelKey: "report.mvWindows.allowlist.link",
        docRef: 23,
      });
    }
    sections.push(section("mvAssistIos", steps));
  }

  if (hasProduct(answers, "tasks")) {
    const steps: VisibleReportSection["steps"] = [
      {
        id: "tasks-sys",
        titleKey: "report.tasks.sysReq.title",
        descriptionKey: "report.tasks.sysReq.description",
        linkLabelKey: "report.tasks.sysReq.link",
        docRef: 32,
      },
    ];
    if (tasksOs.includes("chromeos")) {
      steps.push({
        id: "tasks-start",
        titleKey: "report.tasks.gettingStarted.title",
        descriptionKey: "report.tasks.gettingStarted.description",
        linkLabelKey: "report.tasks.gettingStarted.link",
        docRef: 28,
        href: downloads.tasks,
      });
    }
    if (tasksOs.includes("win")) {
      steps.push({
        id: "tasks-windows-download",
        titleKey: "report.tasks.windowsDownload.title",
        descriptionKey: "report.tasks.windowsDownload.description",
        linkLabelKey: "report.tasks.windowsDownload.link",
        href: tasksWindowsDownloadHref,
      });
      steps.push({
        id: "tasks-windows-msi-parameters",
        titleKey: "report.tasks.windowsDownload.msiDocsLink",
        docRef: 35,
      });
      if (answers.tasks_intune === "yes") {
        steps.push({
          id: "tasks-windows-intune",
          titleKey: "report.tasks.windowsDownload.intune.title",
          descriptionKey: "report.tasks.windowsDownload.intune.description",
          linkLabelKey: "report.tasks.windowsDownload.intune.link",
          docRef: 34,
        });
      }
    }
    if (tasksOs.includes("mac")) {
      steps.push({
        id: "tasks-mac-download",
        titleKey: "report.tasks.macDownload.title",
        descriptionKey: "report.tasks.macDownload.description",
        linkLabelKey: "report.tasks.macDownload.link",
        href: tasksMacDownloadHref,
      });
    }
    if (answers.network_restricted === "yes") {
      steps.push({
        id: "tasks-allowlist",
        titleKey: "report.tasks.allowlist.title",
        descriptionKey: "report.tasks.allowlist.description",
        linkLabelKey: "report.tasks.allowlist.link",
        docRef: 31,
      });
    }
    if (answers.tasks_start_menu === "yes") {
      steps.push({
        id: "tasks-rollout",
        titleKey: "report.tasks.rollOut.title",
        descriptionKey: "report.tasks.rollOut.description",
        linkLabelKey: "report.tasks.rollOut.link",
        docRef: 29,
      });
    }
    sections.push(section("tasks", steps));
  }

  if (hasProduct(answers, "claroread")) {
    sections.push(
      section("claroRead", [
        {
          id: "claro-sys",
          titleKey: "report.claroRead.sysReq.title",
          descriptionKey: "report.claroRead.sysReq.description",
        },
        {
          id: "claro-download",
          titleKey: "report.claroRead.download.title",
          descriptionKey: "report.claroRead.download.description",
          linkLabelKey: "report.claroRead.download.link",
        },
        {
          id: "claro-deploy",
          titleKey: "report.claroRead.deploy.title",
          descriptionKey: "report.claroRead.deploy.description",
          linkLabelKey: "report.claroRead.deploy.link",
        },
      ]),
    );
  }

  sections.push(
    section("support", [
      {
        id: "academy",
        titleKey: "report.support.academy.title",
        descriptionKey: "report.support.academy.description",
        linkLabelKey: "report.support.academy.link",
        href: downloads.academy,
      },
      {
        id: "kb",
        titleKey: "report.support.kb.title",
        descriptionKey: "report.support.kb.description",
        linkLabelKey: "report.support.kb.link",
      },
      {
        id: "contact",
        titleKey: "report.support.contact.title",
      },
      {
        id: "status",
        titleKey: "report.support.status.title",
        descriptionKey: "report.support.status.description",
        linkLabelKey: "report.support.status.link",
      },
    ]),
  );

  return sections;
}

export function createReportId(): string {
  const now = new Date();
  const stamp = now.toISOString().replace(/[-:]/g, "").slice(0, 13);
  return `EW-${stamp}`;
}
